"use client";

import { useCallback, useRef } from "react";
import {
  trackKundaliFieldFunnel,
  type KundaliField,
  type KundaliFieldPhase,
} from "@/lib/analytics-kundali";

/**
 * Field-level funnel emitter (rule 34 owned beacon → events_raw). Fires
 * `field_start` once when a field is first engaged and `field_valid` once when a
 * field first reaches a valid value — per field, deduped via a ref so it cannot
 * re-fire on re-render (rule 47: a presentation/decision event fires ONCE per
 * logical occurrence, never per render). Together with `kundali_field_outcome`
 * (at submit) this gives the per-field start → valid → submit → outcome funnel
 * that finally splits birthPlace ambiguity from true error from network.
 */
export interface FieldFunnel {
  start: (field: KundaliField) => void;
  valid: (field: KundaliField) => void;
}

export function useFieldFunnel(meta: {
  source?: string;
  source_path?: string;
  language?: string;
}): FieldFunnel {
  // Per-field-per-phase fired set — survives re-renders, never re-emits.
  const firedRef = useRef<Set<string>>(new Set());
  // Keep the latest meta in a ref so the callbacks stay reference-stable (so a
  // consumer effect/handler keyed on them doesn't churn) while still reading
  // current source/path/language.
  const metaRef = useRef(meta);
  metaRef.current = meta;

  const emit = useCallback((field: KundaliField, phase: KundaliFieldPhase) => {
    const key = `${field}:${phase}`;
    if (firedRef.current.has(key)) return;
    firedRef.current.add(key);
    trackKundaliFieldFunnel({ field, phase, ...metaRef.current });
  }, []);

  const start = useCallback((field: KundaliField) => emit(field, "start"), [emit]);
  const valid = useCallback((field: KundaliField) => emit(field, "valid"), [emit]);

  return { start, valid };
}
